<template>
  <div>
    <!-- Trigger Button -->
    <v-btn
      v-if="!editItem"
      color="primary"
      class="text-none font-weight-medium"
      depressed
      @click="openDialog"
    >
      <v-icon left>mdi-plus</v-icon>
      New Quotation
    </v-btn>
    <v-btn v-else icon color="primary" @click="openDialog">
      <v-icon>mdi-pencil</v-icon>
    </v-btn>

    <!-- Main Dialog -->
    <v-dialog
      v-model="dialog"
      persistent
      :max-width="dialogMaxWidth"
      scrollable
      content-class="quotation-dialog-transition"
    >
      <v-card class="quotation-dialog" rounded="lg">
        <!-- Dialog Header -->
        <v-card-title class="quotation-dialog__header pa-0">
          <div
            class="d-flex align-center justify-space-between px-6 py-4 w-full"
          >
            <div class="d-flex align-center">
              <v-avatar
                :color="form.quotation_mode === 'government' ? 'blue' : 'green'"
                size="42"
                class="mr-3"
              >
                <v-icon dark>
                  {{
                    form.quotation_mode === "government"
                      ? "mdi-bank"
                      : "mdi-store"
                  }}
                </v-icon>
              </v-avatar>
              <div>
                <h2 class="text-h6 font-weight-bold">
                  {{ editItem ? "Edit" : "New" }} Quotation
                </h2>
                <span class="caption grey--text text--darken-1">
                  {{
                    form.quotation_mode === "government"
                      ? "Government Procurement"
                      : "Walk-in Customer"
                  }}
                </span>
              </div>
            </div>
            <div class="d-flex align-center">
              <v-chip
                :color="form.quotation_mode === 'government' ? 'blue' : 'green'"
                dark
                small
                label
                class="mr-3 font-weight-medium"
              >
                <v-icon left x-small>
                  {{
                    form.quotation_mode === "government"
                      ? "mdi-bank"
                      : "mdi-store"
                  }}
                </v-icon>
                {{ form.quotation_mode === "government" ? "GOV" : "WALK-IN" }}
              </v-chip>
              <v-btn icon @click="dialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
          <v-divider></v-divider>
        </v-card-title>

        <!-- Stepper Progress -->
        <div class="quotation-dialog__stepper-bar px-6 pt-4 pb-2">
          <div class="d-flex align-center justify-center">
            <template v-for="(stepInfo, idx) in stepDefinitions">
              <div
                :key="'step-' + idx"
                class="d-flex align-center cursor-pointer"
                @click="goToStep(idx + 1)"
              >
                <v-avatar
                  :color="
                    step > idx + 1
                      ? 'green'
                      : step === idx + 1
                      ? 'primary'
                      : 'grey lighten-2'
                  "
                  :size="step === idx + 1 ? 36 : 30"
                  class="transition-swing"
                >
                  <v-icon v-if="step > idx + 1" dark small>mdi-check</v-icon>
                  <span
                    v-else
                    :class="
                      step === idx + 1
                        ? 'white--text font-weight-bold'
                        : 'grey--text text--darken-1'
                    "
                    class="caption"
                  >
                    {{ idx + 1 }}
                  </span>
                </v-avatar>
                <span
                  class="ml-2 body-2"
                  :class="
                    step === idx + 1
                      ? 'font-weight-bold primary--text'
                      : 'grey--text text--darken-1'
                  "
                >
                  {{ stepInfo.title }}
                </span>
              </div>
              <v-divider
                v-if="idx < stepDefinitions.length - 1"
                :key="'div-' + idx"
                class="mx-4"
                style="max-width: 80px"
              ></v-divider>
            </template>
          </div>
        </div>

        <v-divider></v-divider>

        <!-- Scrollable Content -->
        <v-card-text
          class="quotation-dialog__body pa-0"
          style="max-height: 65vh; overflow-y: auto"
        >
          <v-window v-model="stepIndex" class="pa-6">
            <!-- ==================== STEP 1: Customer & Mode ==================== -->
            <v-window-item :value="0">
              <v-fade-transition>
                <div>
                  <!-- Mode Selector -->
                  <div class="section-block mb-6">
                    <div class="section-block__header">
                      <v-icon small color="deep-purple" class="mr-2"
                        >mdi-tune-variant</v-icon
                      >
                      <span class="subtitle-2 font-weight-bold"
                        >Quotation Mode</span
                      >
                    </div>
                    <v-card flat class="section-block__body pa-4" rounded="lg">
                      <v-btn-toggle
                        v-model="form.quotation_mode"
                        mandatory
                        borderless
                        class="quotation-mode-toggle"
                        @change="calculateTotals"
                      >
                        <v-btn
                          value="walk_in"
                          class="text-none px-6"
                          :color="
                            form.quotation_mode === 'walk_in'
                              ? 'green lighten-5'
                              : ''
                          "
                          :class="
                            form.quotation_mode === 'walk_in'
                              ? 'green--text'
                              : ''
                          "
                        >
                          <v-icon left small>mdi-store</v-icon>
                          Walk-in
                        </v-btn>
                        <v-btn
                          value="government"
                          class="text-none px-6"
                          :color="
                            form.quotation_mode === 'government'
                              ? 'blue lighten-5'
                              : ''
                          "
                          :class="
                            form.quotation_mode === 'government'
                              ? 'blue--text'
                              : ''
                          "
                        >
                          <v-icon left small>mdi-bank</v-icon>
                          Government
                        </v-btn>
                      </v-btn-toggle>
                    </v-card>
                  </div>

                  <!-- Customer Information -->
                  <div class="section-block mb-6">
                    <div class="section-block__header">
                      <v-icon small color="primary" class="mr-2"
                        >mdi-account-circle</v-icon
                      >
                      <span class="subtitle-2 font-weight-bold"
                        >Customer Information</span
                      >
                    </div>
                    <v-card flat class="section-block__body pa-5" rounded="lg">
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            label="Customer Name"
                            v-model="form.name"
                            prepend-inner-icon="mdi-account"
                            outlined
                            dense
                            :rules="[rules.required]"
                            placeholder="Enter customer name"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            label="Company Name"
                            v-model="form.company_name"
                            prepend-inner-icon="mdi-domain"
                            outlined
                            dense
                            placeholder="Enter company name (optional)"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            label="Address"
                            v-model="form.address"
                            prepend-inner-icon="mdi-map-marker"
                            outlined
                            dense
                            placeholder="Enter address"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            label="Phone Number"
                            v-model="form.phone_number"
                            prepend-inner-icon="mdi-phone"
                            outlined
                            dense
                            placeholder="Enter phone number"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-card>
                  </div>

                  <!-- Details -->
                  <div class="section-block mb-6">
                    <div class="section-block__header">
                      <v-icon small color="orange darken-1" class="mr-2"
                        >mdi-file-document-edit</v-icon
                      >
                      <span class="subtitle-2 font-weight-bold"
                        >Quotation Details</span
                      >
                    </div>
                    <v-card flat class="section-block__body pa-5" rounded="lg">
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            label="Quotation Date"
                            v-model="form.quotation_date"
                            prepend-inner-icon="mdi-calendar"
                            type="date"
                            outlined
                            dense
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="8">
                          <v-textarea
                            label="Notes / Purpose"
                            v-model="form.notes"
                            prepend-inner-icon="mdi-note-text"
                            outlined
                            dense
                            rows="2"
                            auto-grow
                            placeholder="e.g. Purchase of 1 Desktop Computer for Editing Purposes"
                          ></v-textarea>
                        </v-col>
                        <v-col cols="12">
                          <v-textarea
                            label="Remarks"
                            v-model="form.remarks"
                            prepend-inner-icon="mdi-message-text"
                            outlined
                            dense
                            rows="2"
                            auto-grow
                            placeholder="Warranty terms, delivery notes, etc."
                          ></v-textarea>
                        </v-col>
                      </v-row>
                    </v-card>
                  </div>
                </div>
              </v-fade-transition>
            </v-window-item>

            <!-- ==================== STEP 2: Items & Pricing ==================== -->
            <v-window-item :value="1">
              <v-fade-transition>
                <div>
                  <!-- Global Pricing Settings -->
                  <div class="section-block mb-6">
                    <div class="section-block__header">
                      <v-icon small color="teal" class="mr-2">mdi-cog</v-icon>
                      <span class="subtitle-2 font-weight-bold"
                        >Pricing Configuration</span
                      >
                      <v-spacer></v-spacer>
                      <v-chip x-small label outlined color="grey" class="ml-2">
                        Applied to all items
                      </v-chip>
                    </div>
                    <v-card flat class="section-block__body pa-5" rounded="lg">
                      <v-row>
                        <v-col cols="12" sm="6" md="4">
                          <v-text-field
                            label="Markup Percentage"
                            v-model.number="form.markup_percentage"
                            type="number"
                            min="0"
                            outlined
                            dense
                            suffix="%"
                            prepend-inner-icon="mdi-percent"
                            @input="onGlobalSettingChange"
                          >
                            <template v-slot:append>
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                  <v-icon small v-on="on" color="grey"
                                    >mdi-help-circle</v-icon
                                  >
                                </template>
                                <span
                                  >Percentage added on top of supplier
                                  price</span
                                >
                              </v-tooltip>
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                          <v-text-field
                            label="VAT Adjustment (Non-VAT Suppliers)"
                            v-model.number="form.vat_adjustment_percentage"
                            type="number"
                            min="0"
                            outlined
                            dense
                            suffix="%"
                            prepend-inner-icon="mdi-percent"
                            @input="onGlobalSettingChange"
                          >
                            <template v-slot:append>
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                  <v-icon small v-on="on" color="grey"
                                    >mdi-help-circle</v-icon
                                  >
                                </template>
                                <span
                                  >Adjustment added to non-VAT supplier
                                  prices</span
                                >
                              </v-tooltip>
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>
                    </v-card>
                  </div>

                  <!-- Items Table -->
                  <div class="section-block mb-6">
                    <div class="section-block__header">
                      <v-icon small color="indigo" class="mr-2"
                        >mdi-format-list-bulleted</v-icon
                      >
                      <span class="subtitle-2 font-weight-bold"
                        >Quotation Items</span
                      >
                      <v-spacer></v-spacer>
                      <v-chip
                        x-small
                        label
                        :color="currentItems.length > 0 ? 'primary' : 'grey'"
                        dark
                      >
                        {{ currentItems.length }} item{{
                          currentItems.length !== 1 ? "s" : ""
                        }}
                      </v-chip>
                    </div>
                    <quotation-items-table
                      ref="itemsTable"
                      :items="editItems"
                      :markup-percentage="form.markup_percentage"
                      :vat-adjustment-percentage="
                        form.vat_adjustment_percentage
                      "
                      :quotation-mode="form.quotation_mode"
                      @update="onItemsUpdate"
                    />
                  </div>
                </div>
              </v-fade-transition>
            </v-window-item>

            <!-- ==================== STEP 3: Financial & Review ==================== -->
            <v-window-item :value="2">
              <v-fade-transition>
                <div>
                  <!-- SOP Section -->
                  <div class="section-block mb-6">
                    <div class="section-block__header">
                      <v-icon small color="deep-orange" class="mr-2"
                        >mdi-chart-donut</v-icon
                      >
                      <span class="subtitle-2 font-weight-bold"
                        >SOP (Sales Operating Percentage)</span
                      >
                    </div>
                    <v-card flat class="section-block__body pa-5" rounded="lg">
                      <v-row align="center">
                        <v-col cols="12" sm="4">
                          <v-select
                            label="SOP Type"
                            v-model="form.sop_type"
                            :items="sopTypes"
                            outlined
                            dense
                            clearable
                            prepend-inner-icon="mdi-tag"
                            @change="calculateTotals"
                            placeholder="Select SOP type"
                          ></v-select>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <v-text-field
                            :label="
                              form.sop_type === 'percentage'
                                ? 'SOP Value (%)'
                                : 'SOP Amount'
                            "
                            v-model.number="form.sop_value"
                            type="number"
                            min="0"
                            outlined
                            dense
                            :prefix="form.sop_type !== 'percentage' ? '₱' : ''"
                            :suffix="form.sop_type === 'percentage' ? '%' : ''"
                            @input="calculateTotals"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <v-text-field
                            label="Computed SOP"
                            :value="
                              '₱ ' + formatNumber(calculations.sop_amount)
                            "
                            readonly
                            outlined
                            dense
                            filled
                            class="computed-field"
                          ></v-text-field>
                        </v-col>
                      </v-row>

                      <!-- Government: BIR 2307 & Cheque -->
                      <v-expand-transition>
                        <v-row
                          v-if="form.quotation_mode === 'government'"
                          class="mt-1"
                        >
                          <v-col cols="12" sm="6">
                            <v-text-field
                              label="BIR 2307 Withholding (6%)"
                              :value="
                                '₱ ' +
                                formatNumber(calculations.bir_2307_amount)
                              "
                              readonly
                              outlined
                              dense
                              filled
                              class="computed-field"
                              prepend-inner-icon="mdi-file-document"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              label="Cheque Amount"
                              :value="
                                '₱ ' + formatNumber(calculations.cheque_amount)
                              "
                              readonly
                              outlined
                              dense
                              filled
                              class="computed-field"
                              prepend-inner-icon="mdi-checkbook"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-expand-transition>
                    </v-card>
                  </div>

                  <!-- Discount Section (walk-in & government) -->
                  <div class="section-block mb-6">
                    <div class="section-block__header">
                      <v-icon small color="pink darken-1" class="mr-2"
                        >mdi-sale</v-icon
                      >
                      <span class="subtitle-2 font-weight-bold">Discount</span>
                      <v-spacer></v-spacer>
                      <v-chip x-small label outlined color="grey" class="ml-2">
                        Reduces customer total &amp; net profit
                      </v-chip>
                    </div>
                    <v-card flat class="section-block__body pa-5" rounded="lg">
                      <v-row align="center">
                        <v-col cols="12" sm="4">
                          <v-select
                            label="Discount Type"
                            v-model="form.discount_type"
                            :items="discountTypes"
                            outlined
                            dense
                            clearable
                            prepend-inner-icon="mdi-tag"
                            placeholder="No discount"
                            @change="calculateTotals"
                          ></v-select>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <v-text-field
                            :label="
                              form.discount_type === 'percentage'
                                ? 'Discount Value (%)'
                                : 'Discount Amount'
                            "
                            v-model.number="form.discount_value"
                            type="number"
                            min="0"
                            outlined
                            dense
                            :disabled="!form.discount_type"
                            :prefix="
                              form.discount_type === 'figure' ? '₱' : ''
                            "
                            :suffix="
                              form.discount_type === 'percentage' ? '%' : ''
                            "
                            @input="calculateTotals"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <v-text-field
                            label="Computed Discount"
                            :value="
                              '₱ ' + formatNumber(calculations.discount_amount)
                            "
                            readonly
                            outlined
                            dense
                            filled
                            class="computed-field"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-card>
                  </div>

                  <!-- Mode-Specific Settings -->
                  <v-expand-transition>
                    <div
                      v-if="form.quotation_mode === 'government'"
                      class="section-block mb-6"
                    >
                      <div
                        class="
                          section-block__header section-block__header--blue
                        "
                      >
                        <v-icon small color="blue" class="mr-2"
                          >mdi-bank</v-icon
                        >
                        <span class="subtitle-2 font-weight-bold blue--text"
                          >Government Settings</span
                        >
                      </div>
                      <v-card
                        flat
                        class="section-block__body pa-5 blue-border-left"
                        rounded="lg"
                      >
                        <v-row>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              label="ABC (Approved Budget for Contract)"
                              v-model.number="form.abc"
                              type="number"
                              min="0"
                              outlined
                              dense
                              prefix="₱"
                              prepend-inner-icon="mdi-cash"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              label="Income Tax Percentage"
                              v-model.number="form.income_tax_percentage"
                              type="number"
                              min="0"
                              outlined
                              dense
                              suffix="%"
                              prepend-inner-icon="mdi-percent"
                              @input="calculateTotals"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-card>
                    </div>
                  </v-expand-transition>

                  <v-expand-transition>
                    <div
                      v-if="form.quotation_mode === 'walk_in'"
                      class="section-block mb-6"
                    >
                      <div
                        class="
                          section-block__header section-block__header--green
                        "
                      >
                        <v-icon small color="green" class="mr-2"
                          >mdi-store</v-icon
                        >
                        <span class="subtitle-2 font-weight-bold green--text"
                          >Walk-in Settings</span
                        >
                      </div>
                      <v-card
                        flat
                        class="section-block__body pa-5 green-border-left"
                        rounded="lg"
                      >
                        <v-switch
                          v-model="form.is_vat_purchase"
                          dense
                          inset
                          color="green"
                          @change="calculateTotals"
                        >
                          <template v-slot:label>
                            <div>
                              <span class="body-2 font-weight-medium"
                                >VAT Purchase</span
                              >
                              <br />
                              <span class="caption grey--text"
                                >Adds 12% VAT on top</span
                              >
                            </div>
                          </template>
                        </v-switch>
                        <v-expand-transition>
                          <v-row v-if="form.is_vat_purchase" class="mt-1">
                            <v-col cols="12" sm="6">
                              <v-text-field
                                label="Income Tax Percentage"
                                v-model.number="form.income_tax_percentage"
                                type="number"
                                min="0"
                                outlined
                                dense
                                suffix="%"
                                prepend-inner-icon="mdi-percent"
                                hint="Deducted from net profit when issuing an OR"
                                persistent-hint
                                @input="calculateTotals"
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </v-expand-transition>
                      </v-card>
                    </div>
                  </v-expand-transition>

                  <!-- Financial Summary -->
                  <div class="section-block">
                    <div class="section-block__header">
                      <v-icon small color="green darken-1" class="mr-2"
                        >mdi-calculator</v-icon
                      >
                      <span class="subtitle-2 font-weight-bold"
                        >Financial Summary</span
                      >
                    </div>
                    <quotation-summary
                      :mode="form.quotation_mode"
                      :calculations="calculations"
                      :custom-deductions="customDeductions"
                      @add-deduction="openAddDeductionModal"
                      @edit-deduction="openEditDeductionModal"
                      @delete-deduction="deleteDeduction"
                    />
                  </div>
                </div>
              </v-fade-transition>
            </v-window-item>
          </v-window>
        </v-card-text>

        <!-- Image Export (hidden capture area) -->
        <quotation-image-export
          ref="imageExport"
          :form="form"
          :items="currentItems"
          :calculations="calculations"
          :custom-deductions="customDeductions"
          @success="showSnackbar($event, 'success', 'mdi-check-circle')"
          @error="showSnackbar($event, 'error', 'mdi-alert-circle')"
        />

        <!-- Add/Edit Deduction Modal -->
        <add-deduction-modal
          :show.sync="showDeductionModal"
          :edit-deduction="editingDeduction"
          :total-quote-price="calculations.total_quote_price"
          @save="handleSaveDeduction"
          @close="closeDeductionModal"
        />

        <v-divider></v-divider>

        <!-- Dialog Footer / Actions -->
        <v-card-actions class="quotation-dialog__footer px-6 py-4">
          <v-btn v-if="step > 1" text class="text-none" @click="step--">
            <v-icon left small>mdi-arrow-left</v-icon>
            Back
          </v-btn>
          <v-btn
            v-if="step === 3"
            outlined
            color="grey darken-1"
            class="text-none ml-2"
            :loading="imageExportLoading"
            @click="copyQuotationAsImage"
          >
            <v-icon left small>mdi-image-multiple</v-icon>
            Copy as Image
          </v-btn>
          <v-btn
            outlined
            color="amber darken-2"
            class="text-none ml-2"
            :loading="draftLoading"
            @click="saveDraft"
          >
            <v-icon left small>mdi-content-save-edit</v-icon>
            Save as Draft
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text class="text-none mr-2" @click="dialog = false">
            Cancel
          </v-btn>
          <v-btn
            v-if="step < 3"
            color="primary"
            depressed
            class="text-none px-6"
            @click="nextStep"
          >
            Continue
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
          <v-btn
            v-else
            color="primary"
            depressed
            class="text-none px-6"
            :loading="loading"
            @click="confirmSubmit"
          >
            <v-icon left small>mdi-check</v-icon>
            {{ editItem ? "Update Quotation" : "Create Quotation" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="440" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-h6 font-weight-bold pb-1">
          Confirm {{ editItem ? "Update" : "Submission" }}
        </v-card-title>
        <v-card-text class="body-2 grey--text text--darken-2">
          You are about to {{ editItem ? "update" : "create" }} a quotation for
          <strong>{{ form.name }}</strong>
          <span v-if="form.company_name"> ({{ form.company_name }})</span>
          with a total of
          <strong class="primary--text"
            >₱ {{ formatNumber(calculations.total_quote_price) }}</strong
          >. <br /><br />
          Do you want to proceed?
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn text class="text-none" @click="confirmDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="primary"
            depressed
            class="text-none px-4"
            :loading="loading"
            @click="submit"
          >
            <v-icon left small>mdi-check</v-icon>
            Yes, {{ editItem ? "Update" : "Create" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      <div class="d-flex align-center">
        <v-icon dark class="mr-2">{{ snackbar.icon }}</v-icon>
        {{ snackbar.message }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import QuotationItemsTable from "./QuotationItemsTable.vue";
import QuotationSummary from "./QuotationSummary.vue";
import AddDeductionModal from "./AddDeductionModal.vue";
import QuotationImageExport from "./QuotationImageExport.vue";

export default {
  components: {
    QuotationItemsTable,
    QuotationSummary,
    AddDeductionModal,
    QuotationImageExport,
  },
  props: {
    editItem: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dialog: false,
      confirmDialog: false,
      loading: false,
      draftLoading: false,
      imageExportLoading: false,
      step: 1,
      currentItems: [],
      editItems: [],
      // Custom deductions
      customDeductions: [],
      showDeductionModal: false,
      editingDeduction: null,
      stepDefinitions: [
        { title: "Customer Info", icon: "mdi-account" },
        { title: "Items & Pricing", icon: "mdi-format-list-bulleted" },
        { title: "Review & Submit", icon: "mdi-check-circle" },
      ],
      sopTypes: [
        { text: "Percentage", value: "percentage" },
        { text: "Fixed Figure", value: "figure" },
      ],
      discountTypes: [
        { text: "Percentage", value: "percentage" },
        { text: "Fixed Amount", value: "figure" },
      ],
      rules: {
        required: (v) => !!v || "This field is required",
      },
      snackbar: {
        show: false,
        message: "",
        color: "success",
        icon: "mdi-check-circle",
        timeout: 3000,
      },
      form: {
        quotation_mode: "walk_in",
        name: "",
        company_name: "",
        address: "",
        phone_number: "",
        notes: "",
        quotation_date: "",
        remarks: "Warranty: 6 months Parts Replacement.",
        markup_percentage: 30,
        vat_adjustment_percentage: 15,
        sop_type: null,
        sop_value: 0,
        discount_type: null,
        discount_value: 0,
        abc: null,
        income_tax_percentage: 2,
        is_vat_purchase: false,
      },
      calculations: {
        total_quote_price: 0,
        total_capital_expenditure: 0,
        sop_amount: 0,
        total_vat_payable: 0,
        income_tax_percentage: 2,
        income_tax_amount: 0,
        net_profit: 0,
        net_profit_margin: 0,
        is_vat_purchase: false,
        vat_amount_walk_in: 0,
        municipal_tax_amount: 0,
        cheque_amount: 0,
        bir_2307_amount: 0,
        discount_amount: 0,
        customer_total_after_discount: 0,
      },
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    stepIndex: {
      get() {
        return this.step - 1;
      },
      set(val) {
        this.step = val + 1;
      },
    },
    dialogMaxWidth() {
      const widths = { 1: 800, 2: 1600, 3: 1000 };
      return widths[this.step] || 1400;
    },
  },
  methods: {
    ...mapActions({
      add_quotation: "quotation/add_quotation",
      edit_quotation: "quotation/edit_quotation",
    }),
    showSnackbar(message, color = "success", icon = "mdi-check-circle") {
      this.snackbar = { show: true, message, color, icon, timeout: 3000 };
    },
    openDialog() {
      if (this.editItem) {
        this.populateFromEdit();
        this.step = 2;
      } else {
        this.resetForm();
        this.step = 1;
      }
      this.dialog = true;
    },
    goToStep(targetStep) {
      // Allow going back without validation
      if (targetStep < this.step) {
        this.step = targetStep;
        return;
      }

      // For going forward, validate each step in sequence
      if (targetStep > this.step) {
        // Validate step 1 before proceeding past it
        if (this.step === 1 || (this.step < 1 && targetStep > 1)) {
          if (!this.form.name || !this.form.name.trim()) {
            this.showSnackbar(
              "Please enter a customer name.",
              "warning",
              "mdi-alert"
            );
            return;
          }
        }

        // Validate step 2 before proceeding to step 3
        if (targetStep === 3 && this.step <= 2) {
          // First check step 1 if we're on it
          if (this.step === 1) {
            if (!this.form.name || !this.form.name.trim()) {
              this.showSnackbar(
                "Please enter a customer name.",
                "warning",
                "mdi-alert"
              );
              return;
            }
          }
          // Then check step 2
          if (this.currentItems.length === 0) {
            this.showSnackbar(
              "Please add at least one item.",
              "warning",
              "mdi-alert"
            );
            return;
          }
        }

        this.step = targetStep;
      }
    },
    nextStep() {
      // Validate current step before proceeding
      if (this.step === 1) {
        if (!this.form.name || !this.form.name.trim()) {
          this.showSnackbar(
            "Please enter a customer name.",
            "warning",
            "mdi-alert"
          );
          return;
        }
      }
      if (this.step === 2) {
        if (this.currentItems.length === 0) {
          this.showSnackbar(
            "Please add at least one item.",
            "warning",
            "mdi-alert"
          );
          return;
        }
      }
      this.step++;
    },
    resetForm() {
      this.form = {
        quotation_mode: "walk_in",
        name: "",
        company_name: "",
        address: "",
        phone_number: "",
        notes: "",
        quotation_date: moment().format("YYYY-MM-DD"),
        remarks: "Warranty: 6 months Parts Replacement.",
        markup_percentage: 30,
        vat_adjustment_percentage: 15,
        sop_type: null,
        sop_value: 0,
        discount_type: null,
        discount_value: 0,
        abc: null,
        income_tax_percentage: 2,
        is_vat_purchase: false,
      };
      this.currentItems = [];
      this.editItems = [];
      this.customDeductions = [];
      this.calculations = {
        total_quote_price: 0,
        total_capital_expenditure: 0,
        sop_amount: 0,
        total_vat_payable: 0,
        income_tax_percentage: 2,
        income_tax_amount: 0,
        net_profit: 0,
        net_profit_margin: 0,
        is_vat_purchase: false,
        vat_amount_walk_in: 0,
        municipal_tax_amount: 0,
        cheque_amount: 0,
        bir_2307_amount: 0,
        discount_amount: 0,
        customer_total_after_discount: 0,
        total_custom_deductions: 0,
      };
    },
    populateFromEdit() {
      const q = this.editItem;
      this.form = {
        id: q.id,
        quotation_mode: q.quotation_mode || "walk_in",
        name: q.customer_name,
        company_name: q.company_name,
        address: q.address,
        phone_number: q.phone_number,
        notes: q.notes,
        quotation_date: moment(q.quotation_date, "MMMM D, YYYY").format(
          "YYYY-MM-DD"
        ),
        remarks: q.remarks,
        markup_percentage: parseFloat(q.markup_percentage) || 30,
        vat_adjustment_percentage:
          parseFloat(q.vat_adjustment_percentage) || 15,
        sop_type: q.sop_type,
        sop_value: parseFloat(q.sop_value) || 0,
        discount_type: q.discount_type || null,
        discount_value: parseFloat(q.discount_value) || 0,
        abc: q.abc ? parseFloat(q.abc) : null,
        income_tax_percentage: parseFloat(q.income_tax_percentage) || 2,
        is_vat_purchase: q.is_vat_purchase || false,
      };
      this.editItems = (q.quote_items || []).map((item) => ({
        item_type: item.item_type || "Hardware",
        quantity: parseInt(item.quantity) || 1,
        unit: item.unit || "pc",
        item_name: item.item_name || "",
        supplier: item.supplier || "",
        supplier_price: parseFloat(item.supplier_price) || 0,
        is_vat: item.is_vat || false,
        freight_in_per_unit: parseFloat(item.freight_in_per_unit) || 0,
        final_supplier_price: parseFloat(item.final_supplier_price) || 0,
        cost_per_unit: parseFloat(item.cost_per_unit) || 0,
        final_quote_price_per_unit:
          parseFloat(item.final_quote_price_per_unit) || 0,
        final_quote_price_total: parseFloat(item.final_quote_price_total) || 0,
        total_amount: parseFloat(item.total_amount) || 0,
        bundle_id: item.bundle_id || null,
        bundle_name: item.bundle_name || null,
        bundle_type: item.bundle_type || null,
        bundle_sort_order: item.bundle_sort_order || 0,
      }));
      this.currentItems = [...this.editItems];
      // Load custom deductions from the quotation
      this.customDeductions = Array.isArray(q.custom_deductions)
        ? q.custom_deductions.map((ded) => ({
            id: ded.id || this.generateDeductionId(),
            name: ded.name || "",
            type: ded.type || "fixed",
            value: parseFloat(ded.value) || 0,
            computed_amount: parseFloat(ded.computed_amount) || 0,
            category: ded.category || "other",
            deduction_mode: ded.deduction_mode || "auto_deduct",
            notes: ded.notes || "",
          }))
        : [];
      this.$nextTick(() => this.calculateTotals());
    },
    onGlobalSettingChange() {
      this.$nextTick(() => this.calculateTotals());
    },
    onItemsUpdate(items) {
      this.currentItems = items;
      this.calculateTotals();
    },
    calculateTotals() {
      const items = this.currentItems;
      const mode = this.form.quotation_mode;

      const totalQuotePrice = items.reduce(
        (sum, item) => sum + (parseFloat(item.final_quote_price_total) || 0),
        0
      );

      const totalCapitalExpenditure = items.reduce((sum, item) => {
        const sp = parseFloat(item.supplier_price) || 0;
        const fi = parseFloat(item.freight_in_per_unit) || 0;
        const qty = parseFloat(item.quantity) || 0;
        return sum + sp * qty + fi * qty;
      }, 0);

      const vatableAmount = totalQuotePrice / 1.12;
      const totalVat12 = totalQuotePrice - vatableAmount;
      const bir2307Amount = totalVat12 / 2;
      const chequeAmount = totalQuotePrice - bir2307Amount;

      let sopAmount = 0;
      if (this.form.sop_type === "percentage") {
        sopAmount =
          (chequeAmount * (parseFloat(this.form.sop_value) || 0)) / 100;
      } else if (this.form.sop_type === "figure") {
        sopAmount = parseFloat(this.form.sop_value) || 0;
      }

      // Discount: a price reduction given to the customer. It lowers both the
      // amount the customer pays and the net profit. Computed off the total
      // quote price (percentage) or entered as a fixed amount. Applies to both
      // walk-in and government modes.
      let discountAmount = 0;
      if (this.form.discount_type === "percentage") {
        discountAmount =
          (totalQuotePrice * (parseFloat(this.form.discount_value) || 0)) / 100;
      } else if (this.form.discount_type === "figure") {
        discountAmount = parseFloat(this.form.discount_value) || 0;
      }

      // Recalculate custom deduction computed amounts for percentage-based deductions
      // and split into auto-deducted vs capital expenditure
      let totalCustomDeductions = 0;
      let capexCustomDeductions = 0;
      let autoDeductCustomDeductions = 0;
      this.customDeductions.forEach((ded) => {
        if (ded.type === "percentage") {
          ded.computed_amount =
            (totalQuotePrice * (parseFloat(ded.value) || 0)) / 100;
        } else {
          ded.computed_amount = parseFloat(ded.value) || 0;
        }
        totalCustomDeductions += ded.computed_amount;
        if (ded.deduction_mode === "capital_expenditure") {
          capexCustomDeductions += ded.computed_amount;
        } else {
          autoDeductCustomDeductions += ded.computed_amount;
        }
      });

      // Capital expenditure includes item costs + capex custom deductions
      const displayCapitalExpenditure =
        totalCapitalExpenditure + capexCustomDeductions;

      let netProfit = 0;
      let netProfitMargin = 0;
      let totalVatPayable = 0;
      let incomeTaxAmount = 0;
      let vatAmountWalkIn = 0;
      let municipalTaxAmount = 0;

      if (mode === "government") {
        totalVatPayable = (totalQuotePrice / 1.12) * 0.12;
        incomeTaxAmount =
          totalQuotePrice *
          ((parseFloat(this.form.income_tax_percentage) || 0) / 100);
        // Net profit uses combined CapEx + all custom deductions (both types)
        // = displayCapitalExpenditure + autoDeductCustomDeductions
        // = (totalCapitalExpenditure + capexCustomDeductions) + autoDeductCustomDeductions
        // = totalCapitalExpenditure + totalCustomDeductions  (unchanged)
        netProfit =
          totalQuotePrice -
          displayCapitalExpenditure -
          sopAmount -
          incomeTaxAmount -
          totalVatPayable -
          autoDeductCustomDeductions -
          discountAmount;
        netProfitMargin =
          totalQuotePrice > 0 ? (netProfit / totalQuotePrice) * 100 : 0;
      } else {
        if (this.form.is_vat_purchase) {
          vatAmountWalkIn = totalQuotePrice * 0.12;
          incomeTaxAmount =
            totalQuotePrice *
            ((parseFloat(this.form.income_tax_percentage) || 0) / 100);
        }
        netProfit =
          totalQuotePrice -
          displayCapitalExpenditure -
          sopAmount -
          incomeTaxAmount -
          autoDeductCustomDeductions -
          discountAmount;
        netProfitMargin =
          totalQuotePrice > 0 ? (netProfit / totalQuotePrice) * 100 : 0;
      }

      // What the customer pays after the discount (gross of VAT for walk-in VAT
      // purchases, which add 12% on top of the quote price).
      const customerBaseTotal =
        mode === "walk_in" ? totalQuotePrice + vatAmountWalkIn : totalQuotePrice;
      const customerTotalAfterDiscount = customerBaseTotal - discountAmount;

      // Effective markup = selling price over cost (capital expenditure)
      const markup =
        displayCapitalExpenditure > 0
          ? ((totalQuotePrice - displayCapitalExpenditure) /
              displayCapitalExpenditure) *
            100
          : 0;

      this.calculations = {
        total_quote_price: totalQuotePrice,
        total_capital_expenditure: displayCapitalExpenditure,
        item_capital_expenditure: totalCapitalExpenditure,
        capex_custom_deductions: capexCustomDeductions,
        auto_deduct_custom_deductions: autoDeductCustomDeductions,
        sop_amount: sopAmount,
        total_vat_payable: totalVatPayable,
        income_tax_percentage: this.form.income_tax_percentage,
        income_tax_amount: incomeTaxAmount,
        net_profit: netProfit,
        net_profit_margin: netProfitMargin,
        markup: markup,
        is_vat_purchase: this.form.is_vat_purchase,
        vat_amount_walk_in: vatAmountWalkIn,
        municipal_tax_amount: municipalTaxAmount,
        cheque_amount: chequeAmount,
        bir_2307_amount: bir2307Amount,
        discount_type: this.form.discount_type,
        discount_value: this.form.discount_value,
        discount_amount: discountAmount,
        customer_total_after_discount: customerTotalAfterDiscount,
        total_custom_deductions: totalCustomDeductions,
      };
    },
    buildPayload(isDraft = false) {
      return {
        id: this.form.id || undefined,
        name: this.form.name,
        company_name: this.form.company_name,
        address: this.form.address,
        phone_number: this.form.phone_number,
        notes: this.form.notes,
        quotation_date: this.form.quotation_date,
        remarks: this.form.remarks,
        sub_total: this.calculations.total_quote_price,
        vat:
          this.calculations.total_vat_payable ||
          this.calculations.vat_amount_walk_in,
        discount: this.calculations.discount_amount || 0,
        discount_type: this.form.discount_type,
        discount_value: this.form.discount_value || 0,
        total: this.calculations.total_quote_price,
        margin_percentage: this.form.markup_percentage,
        total_capital_amount: this.calculations.total_capital_expenditure,
        quotation_mode: this.form.quotation_mode,
        markup_percentage: this.form.markup_percentage,
        vat_adjustment_percentage: this.form.vat_adjustment_percentage,
        sop_type: this.form.sop_type,
        sop_value: this.form.sop_value,
        sop_amount: this.calculations.sop_amount,
        abc: this.form.abc,
        income_tax_percentage: this.form.income_tax_percentage,
        income_tax_amount: this.calculations.income_tax_amount,
        total_vat_payable: this.calculations.total_vat_payable,
        total_capital_expenditure: this.calculations.total_capital_expenditure,
        net_profit: this.calculations.net_profit,
        net_profit_margin: this.calculations.net_profit_margin,
        is_vat_purchase: this.form.is_vat_purchase,
        municipal_tax_amount: this.calculations.municipal_tax_amount,
        vat_amount_walk_in: this.calculations.vat_amount_walk_in,
        total_quote_price: this.calculations.total_quote_price,
        cheque_amount: this.calculations.cheque_amount,
        bir_2307_amount: this.calculations.bir_2307_amount,
        custom_deductions: this.customDeductions,
        total_custom_deductions: this.calculations.total_custom_deductions,
        is_draft: isDraft,
        items: isDraft
          ? this.currentItems
          : this.currentItems.filter(
              (i) => i.item_name && i.item_name.trim() !== ""
            ),
      };
    },
    confirmSubmit() {
      if (!this.form.name || !this.form.name.trim()) {
        this.showSnackbar(
          "Customer name is required.",
          "error",
          "mdi-alert-circle"
        );
        this.step = 1;
        return;
      }
      if (this.currentItems.length === 0) {
        this.showSnackbar(
          "Please add at least one item.",
          "error",
          "mdi-alert-circle"
        );
        this.step = 2;
        return;
      }
      this.confirmDialog = true;
    },
    async submit() {
      this.loading = true;
      try {
        const payload = this.buildPayload(false);
        if (this.editItem) {
          await this.edit_quotation(payload);
          this.showSnackbar(
            "Quotation updated successfully!",
            "success",
            "mdi-check-circle"
          );
        } else {
          await this.add_quotation(payload);
          this.showSnackbar(
            "Quotation created successfully!",
            "success",
            "mdi-check-circle"
          );
        }
        this.confirmDialog = false;
        this.dialog = false;
      } catch (err) {
        this.showSnackbar(
          "Error: " + (err.message || "Something went wrong"),
          "error",
          "mdi-alert-circle"
        );
      } finally {
        this.loading = false;
      }
    },
    async saveDraft() {
      this.draftLoading = true;
      try {
        const payload = this.buildPayload(true);
        if (this.editItem || this.form.id) {
          await this.edit_quotation(payload);
          this.showSnackbar(
            "Draft updated successfully!",
            "success",
            "mdi-content-save-edit"
          );
        } else {
          const created = await this.add_quotation(payload);
          if (created && created.id) {
            this.form.id = created.id;
          }
          this.showSnackbar(
            "Quotation saved as draft!",
            "success",
            "mdi-content-save-edit"
          );
        }
      } catch (err) {
        this.showSnackbar(
          "Error saving draft: " + (err.message || "Something went wrong"),
          "error",
          "mdi-alert-circle"
        );
      } finally {
        this.draftLoading = false;
      }
    },
    // ==================== Custom Deduction Methods ====================
    generateDeductionId() {
      return (
        "ded_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9)
      );
    },
    openAddDeductionModal() {
      this.editingDeduction = null;
      this.showDeductionModal = true;
    },
    openEditDeductionModal(deduction) {
      this.editingDeduction = { ...deduction };
      this.showDeductionModal = true;
    },
    closeDeductionModal() {
      this.showDeductionModal = false;
      this.editingDeduction = null;
    },
    handleSaveDeduction(deduction) {
      const existingIndex = this.customDeductions.findIndex(
        (d) => d.id === deduction.id
      );
      if (existingIndex >= 0) {
        // Update existing deduction using Vue.set for reactivity
        this.$set(this.customDeductions, existingIndex, deduction);
      } else {
        // Add new deduction
        this.customDeductions.push(deduction);
      }
      this.calculateTotals();
      this.closeDeductionModal();
    },
    deleteDeduction(deduction) {
      const index = this.customDeductions.findIndex(
        (d) => d.id === deduction.id
      );
      if (index >= 0) {
        this.customDeductions.splice(index, 1);
        this.calculateTotals();
      }
    },
    formatNumber(val) {
      const num = parseFloat(val) || 0;
      return num.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async copyQuotationAsImage() {
      this.imageExportLoading = true;
      try {
        await this.$refs.imageExport.copyAsImage();
      } finally {
        this.imageExportLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.quotation-dialog {
  overflow: hidden;

  .w-full {
    width: 100%;
  }

  &__header {
    background: #fafbfc;
  }

  &__stepper-bar {
    background: #fff;
  }

  &__footer {
    background: #fafbfc;
  }
}

/* Section block styling */
.section-block {
  &__header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    padding: 0 4px;

    &--blue {
      border-left: 3px solid #1976d2;
      padding-left: 8px;
    }

    &--green {
      border-left: 3px solid #4caf50;
      padding-left: 8px;
    }
  }

  &__body {
    border: 1px solid #e8eaed;
    background: #fafbfc;
  }
}

.blue-border-left {
  border-left: 3px solid #1976d2 !important;
}

.green-border-left {
  border-left: 3px solid #4caf50 !important;
}

.computed-field {
  .v-input__slot {
    background: #f5f5f5 !important;
  }
}

/* Quotation mode toggle */
.quotation-mode-toggle {
  border-radius: 8px !important;
  overflow: hidden;

  .v-btn {
    border-radius: 8px !important;
    letter-spacing: 0;
  }
}

/* Transition helpers */
.transition-swing {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

/* Cursor pointer */
.cursor-pointer {
  cursor: pointer;
}
</style>

<style lang="scss">
/* Unscoped: targets the v-dialog portal rendered at body level */
.quotation-dialog-transition > .v-dialog {
  transition: max-width 0.3s ease-in-out;
}
</style>
